import { motion } from "motion/react";
import {
    User,
    Shield,
    Bell,
    Globe,
    Key,
    Trash2,
    Download,
    Check,
    Copy,
    Eye,
    EyeOff,
    RefreshCw,
    AlertTriangle
} from "lucide-react";
import { useState } from "react";

import { useToast } from "../components/ui-feedback";
import { useRWAStore } from "../store/rwa-store";

type SettingsTab = 'profile' | 'security' | 'notifications' | 'preferences' | 'api';

export default function SettingsView() {
    const { currentUser, resetDemo } = useRWAStore();
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(currentUser?.preferences?.twoFactorEnabled || false);
    const [notifications, setNotifications] = useState({
        email: true,
        push: true,
        priceAlerts: true,
        dividends: true,
        security: true
    });
    const [showApiKey, setShowApiKey] = useState(false);
    const [apiKeys] = useState([
        { id: 'key-1', name: 'Production Key', key: 'rwa_live_xxxxxxxxxxxxxxxxxxxxx', created: '2024-10-15', lastUsed: '2024-12-19' },
        { id: 'key-2', name: 'Development Key', key: 'rwa_test_yyyyyyyyyyyyyyyyyyyyy', created: '2024-11-01', lastUsed: '2024-12-18' }
    ]);
    const [copied, setCopied] = useState<string | null>(null);

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
    };

    const tabs: { id: SettingsTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'preferences', label: 'Preferences', icon: Globe },
        { id: 'api', label: 'API Keys', icon: Key },
    ];

    if (!currentUser) return null;

    return (
        <div className="max-w-5xl mx-auto">
            <header className="mb-8">
                <h1 className="text-3xl font-bold tracking-tight mb-2">Settings</h1>
                <p className="text-neutral-400">Manage your account settings and preferences</p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Sidebar */}
                <div className="lg:col-span-1">
                    <nav className="space-y-1">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === tab.id
                                    ? 'bg-white text-black'
                                    : 'text-neutral-400 hover:bg-neutral-900 hover:text-white'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>

                    {/* Danger Zone */}
                    <div className="mt-8 pt-8 border-t border-neutral-800">
                        <button
                            onClick={() => {
                                if (confirm('Are you sure you want to reset the demo? This will clear all data.')) {
                                    resetDemo();
                                }
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-500/10 transition-all"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Reset Demo
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-3">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6"
                    >
                        {/* Profile Tab */}
                        {activeTab === 'profile' && (
                            <div className="space-y-6">
                                <h2 className="text-lg font-bold mb-6">Profile Information</h2>

                                <div className="flex items-center gap-6 mb-8">
                                    <div className="w-20 h-20 rounded-2xl bg-linear-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-2xl font-bold">
                                        {currentUser.name.charAt(0)}
                                    </div>
                                    <div>
                                        <button className="px-4 py-2 bg-neutral-800 rounded-lg text-sm font-medium hover:bg-neutral-700 transition-colors">
                                            Change Avatar
                                        </button>
                                        <p className="text-xs text-neutral-500 mt-2">JPG, PNG up to 2MB</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Full Name</label>
                                        <input
                                            type="text"
                                            defaultValue={currentUser.name}
                                            className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-3 px-4 focus:outline-hidden focus:border-neutral-700"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Email</label>
                                        <input
                                            type="email"
                                            defaultValue={currentUser.email}
                                            className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-3 px-4 focus:outline-hidden focus:border-neutral-700"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Phone</label>
                                        <input
                                            type="tel"
                                            defaultValue={currentUser.phone || ''}
                                            placeholder="+1 (555) 000-0000"
                                            className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-3 px-4 focus:outline-hidden focus:border-neutral-700"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Role</label>
                                        <div className="py-3 px-4 bg-neutral-950 border border-neutral-800 rounded-xl text-neutral-500 capitalize">
                                            {currentUser.role}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <button
                                        onClick={() => showToast('success', 'Profile Updated', 'Your profile changes have been saved.')}
                                        className="px-6 py-2.5 bg-emerald-500 text-black font-bold rounded-xl hover:bg-emerald-400 transition-colors"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <div className="space-y-6">
                                <h2 className="text-lg font-bold mb-6">Security Settings</h2>

                                {/* Password Change */}
                                <div className="p-6 bg-neutral-950 rounded-xl border border-neutral-800">
                                    <h3 className="font-medium mb-4">Change Password</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm text-neutral-400 mb-2">Current Password</label>
                                            <input type="password" className="w-full bg-neutral-900 border border-neutral-800 rounded-lg py-2.5 px-4 focus:outline-hidden focus:border-neutral-700" />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-neutral-400 mb-2">New Password</label>
                                            <input type="password" className="w-full bg-neutral-900 border border-neutral-800 rounded-lg py-2.5 px-4 focus:outline-hidden focus:border-neutral-700" />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-neutral-400 mb-2">Confirm New Password</label>
                                            <input type="password" className="w-full bg-neutral-900 border border-neutral-800 rounded-lg py-2.5 px-4 focus:outline-hidden focus:border-neutral-700" />
                                        </div>
                                        <button
                                            onClick={() => showToast('success', 'Password Updated', 'Your password has been changed successfully.')}
                                            className="px-4 py-2 bg-neutral-800 rounded-lg text-sm font-medium hover:bg-neutral-700 transition-colors"
                                        >
                                            Update Password
                                        </button>
                                    </div>
                                </div>

                                {/* 2FA Toggle */}
                                <div className="p-6 bg-neutral-950 rounded-xl border border-neutral-800 flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium mb-1">Two-Factor Authentication</h3>
                                        <p className="text-sm text-neutral-400">Add an extra layer of security to your account</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setTwoFactorEnabled(!twoFactorEnabled);
                                            showToast('success', '2FA ' + (!twoFactorEnabled ? 'Enabled' : 'Disabled'), !twoFactorEnabled ? 'Two-factor authentication is now active.' : 'Two-factor authentication has been turned off.');
                                        }}
                                        className={`relative w-14 h-7 rounded-full transition-colors ${twoFactorEnabled ? 'bg-emerald-500' : 'bg-neutral-700'}`}
                                    >
                                        <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${twoFactorEnabled ? 'left-8' : 'left-1'}`} />
                                    </button>
                                </div>

                                {/* Sessions */}
                                <div className="p-6 bg-neutral-950 rounded-xl border border-neutral-800">
                                    <h3 className="font-medium mb-4">Active Sessions</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between p-3 bg-neutral-900 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                                <div>
                                                    <div className="text-sm font-medium">Current Session</div>
                                                    <div className="text-xs text-neutral-500">Chrome on macOS • New York, US</div>
                                                </div>
                                            </div>
                                            <span className="text-xs text-emerald-500">Active now</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Notifications Tab */}
                        {activeTab === 'notifications' && (
                            <div className="space-y-6">
                                <h2 className="text-lg font-bold mb-6">Notification Preferences</h2>

                                {[
                                    { key: 'email', label: 'Email Notifications', desc: 'Receive updates via email' },
                                    { key: 'push', label: 'Push Notifications', desc: 'Browser push notifications' },
                                    { key: 'priceAlerts', label: 'Price Alerts', desc: 'Get notified about price movements' },
                                    { key: 'dividends', label: 'Dividend Updates', desc: 'Notifications for yield distributions' },
                                    { key: 'security', label: 'Security Alerts', desc: 'Critical security notifications' },
                                ].map(item => (
                                    <div key={item.key} className="p-4 bg-neutral-950 rounded-xl border border-neutral-800 flex items-center justify-between">
                                        <div>
                                            <h3 className="font-medium mb-0.5">{item.label}</h3>
                                            <p className="text-sm text-neutral-400">{item.desc}</p>
                                        </div>
                                        <button
                                            onClick={() => setNotifications(n => ({ ...n, [item.key]: !n[item.key as keyof typeof n] }))}
                                            className={`relative w-12 h-6 rounded-full transition-colors ${notifications[item.key as keyof typeof notifications] ? 'bg-emerald-500' : 'bg-neutral-700'}`}
                                        >
                                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${notifications[item.key as keyof typeof notifications] ? 'left-7' : 'left-1'}`} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Preferences Tab */}
                        {activeTab === 'preferences' && (
                            <div className="space-y-6">
                                <h2 className="text-lg font-bold mb-6">App Preferences</h2>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Language</label>
                                        <select className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-3 px-4 focus:outline-hidden focus:border-neutral-700">
                                            <option value="en">English (US)</option>
                                            <option value="es">Español</option>
                                            <option value="fr">Français</option>
                                            <option value="de">Deutsch</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Timezone</label>
                                        <select className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-3 px-4 focus:outline-hidden focus:border-neutral-700">
                                            <option value="utc">UTC</option>
                                            <option value="est">Eastern Time (EST)</option>
                                            <option value="pst">Pacific Time (PST)</option>
                                            <option value="gmt">GMT</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Currency Display</label>
                                        <select className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-3 px-4 focus:outline-hidden focus:border-neutral-700">
                                            <option value="usd">USD ($)</option>
                                            <option value="eur">EUR (€)</option>
                                            <option value="gbp">GBP (£)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-400 mb-2">Date Format</label>
                                        <select className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-3 px-4 focus:outline-hidden focus:border-neutral-700">
                                            <option value="mdy">MM/DD/YYYY</option>
                                            <option value="dmy">DD/MM/YYYY</option>
                                            <option value="ymd">YYYY-MM-DD</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <button
                                        onClick={() => showToast('success', 'Preferences Saved', 'Your preferences have been updated.')}
                                        className="px-6 py-2.5 bg-emerald-500 text-black font-bold rounded-xl hover:bg-emerald-400 transition-colors"
                                    >
                                        Save Preferences
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* API Keys Tab */}
                        {activeTab === 'api' && (
                            <div className="space-y-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-lg font-bold">API Keys</h2>
                                    <button className="px-4 py-2 bg-emerald-500 text-black font-medium rounded-xl hover:bg-emerald-400 transition-colors text-sm">
                                        Generate New Key
                                    </button>
                                </div>

                                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-3 mb-6">
                                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <p className="text-amber-500 font-medium">Keep your API keys secure</p>
                                        <p className="text-amber-500/70">Never share your keys or expose them in client-side code.</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {apiKeys.map(apiKey => (
                                        <div key={apiKey.id} className="p-4 bg-neutral-950 rounded-xl border border-neutral-800">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h3 className="font-medium">{apiKey.name}</h3>
                                                    <p className="text-xs text-neutral-500">Created {apiKey.created} • Last used {apiKey.lastUsed}</p>
                                                </div>
                                                <button className="text-red-500 hover:text-red-400 p-1">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 bg-neutral-900 rounded-lg py-2 px-3 font-mono text-sm overflow-hidden">
                                                    {showApiKey ? apiKey.key : '•'.repeat(32)}
                                                </div>
                                                <button
                                                    onClick={() => setShowApiKey(!showApiKey)}
                                                    className="p-2 hover:bg-neutral-800 rounded-lg"
                                                >
                                                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                                <button
                                                    onClick={() => handleCopy(apiKey.key, apiKey.id)}
                                                    className="p-2 hover:bg-neutral-800 rounded-lg"
                                                >
                                                    {copied === apiKey.id ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>

                    {/* Account Actions */}
                    <div className="mt-6 p-6 bg-neutral-900/40 border border-neutral-800 rounded-2xl">
                        <h3 className="font-bold mb-4">Account Actions</h3>
                        <div className="flex flex-wrap gap-3">
                            <button className="flex items-center gap-2 px-4 py-2 bg-neutral-800 rounded-lg text-sm hover:bg-neutral-700 transition-colors">
                                <Download className="w-4 h-4" />
                                Export Data
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-500 rounded-lg text-sm hover:bg-red-500/20 transition-colors">
                                <Trash2 className="w-4 h-4" />
                                Delete Account
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
